import { FC, useEffect, useState } from 'react';
import { Card, CardGroup } from 'rsuite';
import styles from './rtbCampaignSelectAdType.module.scss';
import { Section } from 'components/Rsuite/Section';
import { AdType } from 'core/rtbCampaign/RtbCampaign';
import { useCampaignStore } from 'store/campaignStore';
import { useHistory, useLocation } from 'react-router-dom';
import DisplayLogo from 'assets/adType_display.svg';
import KeywordLogo from 'assets/adType_keyword.svg';
import ShopLogo from 'assets/adType_shop.svg';
import RecommendLogo from 'assets/adType_recommend.svg';
import OutboundKeywordLogo from 'assets/adType_network.svg';
import NewLogo from 'assets/new.svg';
import { useCoreContext } from 'contexts/coreContext';
import _ from 'lodash';
import { useCallAPI } from 'hooks/useCallAPI';
import { DefaultBrandManager } from 'core/brand/BrandManager';
import InfoOutlineIcon from '@rsuite/icons/InfoOutline';

const defaultBrandManager = new DefaultBrandManager();

const RtbCampaignSelectAdType: FC = () => {
  const campaignType = [
    {
      title: '關鍵字廣告',
      description: (
        <>
          <p className={styles['adTypeCard--description']}>
            捕捉對相似產品有興趣的用戶，
            <br />
            吸引更多潛在新客
            <a
              href='https://bit.ly/4l0JOw4'
              target='_blank'
              rel='noreferrer'
              className='ml-2 font-bold underline hover:font-bold'
            >
              了解更多
            </a>
          </p>
        </>
      ),
      logo: KeywordLogo,
      adType: AdType.MAX_CVR_KEYWORD,
      permission: 'maxCVRKeyword',
      isNew: false,
      lockHint: '',
    },
    {
      title: '黃金置頂搜尋廣告',
      description: (
        <>
          <p className={styles['adTypeCard--description']}>
            搶佔搜尋結果首屏位置，
            <br />
            強勢置頂高潛熱品
            <a
              href='https://bit.ly/4i7ilXP'
              target='_blank'
              rel='noreferrer'
              className='ml-2 font-bold underline hover:font-bold'
            >
              了解更多
            </a>
          </p>
        </>
      ),
      logo: ShopLogo,
      adType: AdType.SHOP,
      permission: 'shop',
      isNew: false,
      lockHint: '廠商所有品牌未達投放標準，無法使用黃金置頂搜尋廣告',
    },
    {
      title: '展示型廣告',
      description: (
        <>
          <p className={styles['adTypeCard--description']}>
            根據受眾、產品進行投遞，
            <br />
            提高消費者關注度及購買意願
            <a
              href='https://bit.ly/43TPTUT'
              target='_blank'
              rel='noreferrer'
              className='ml-2 font-bold underline hover:font-bold'
            >
              了解更多
            </a>
          </p>
        </>
      ),
      logo: DisplayLogo,
      adType: AdType.DISPLAY,
      permission: 'bypass',
      isNew: false,
      lockHint: '',
    },
    {
      title: '推薦廣告',
      description: (
        <>
          <p className={styles['adTypeCard--description']}>
            廣告出現於消費者主動搜尋的結果頁，提供AI技術自動選品、選字與出價
            <a
              href='https://bit.ly/3DMTN7C'
              target='_blank'
              rel='noreferrer'
              className='ml-2 font-bold underline hover:font-bold'
            >
              了解更多
            </a>
          </p>
        </>
      ),
      logo: RecommendLogo,
      adType: AdType.RECOMMEND,
      permission: 'recommend',
      isNew: false,
      lockHint: '廠商所有品牌未達投放標準，無法使用推薦廣告',
    },
    {
      title: '聯播網廣告',
      description: (
        <>
          <p className={styles['adTypeCard--description']}>
            站內站外智能投放，吸引高潛力轉換用戶
            <br />
            <a
              href='https://bit.ly/3YApIzm'
              target='_blank'
              rel='noreferrer'
              className='ml-2 font-bold underline hover:font-bold'
            >
              了解更多
            </a>
          </p>
        </>
      ),
      logo: OutboundKeywordLogo,
      adType: AdType.OUTBOUND_KEYWORD,
      permission: 'outboundKeyword',
      isNew: true,
      lockHint: '',
    },
  ];
  const history = useHistory();
  const location = useLocation();
  const core = useCoreContext();
  const { callAPIs } = useCallAPI();
  const { handleLoading } = useCampaignStore();
  const [lockShop, setLockShop] = useState(false);
  const [lockRecommend, setLockRecommend] = useState(false);
  const campaignPermission: { [key: string]: boolean } = _.get(
    core,
    'accountManager.localeMeta.addonFeatures.campaign',
    {},
  );
  const checkPermission = (list) => {
    return list.filter(
      (item) =>
        campaignPermission[item.permission] || item.permission === 'bypass',
    );
  };
  const selectdAdType = (type) => {
    if (type.adType === AdType.SHOP && lockShop) {
      return;
    }
    if (type.adType === AdType.RECOMMEND && lockRecommend) {
      return;
    }
    useCampaignStore.setState({
      adType: type.adType,
    });
    return history.push(location.pathname.replace('/select-adType', '/create'));
  };
  // 取得品牌選項
  const fetchBrandOptions = async (): Promise<void> => {
    try {
      handleLoading(true);
      await callAPIs(
        [defaultBrandManager.getBrandOptions.bind(defaultBrandManager)],
        (brandOptions) => {
          // 必須過濾掉"其他"品牌
          const preprocessBrandOptions = brandOptions.filter(
            ({ value }) => value !== '99999999999999999',
          );
          if (preprocessBrandOptions.length === 0) {
            setLockShop(true);
          } else {
            setLockShop(false);
          }
        },
      );
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      handleLoading(false);
    }
  };

  const fetchRecommendBrandOptions = async (): Promise<void> => {
    try {
      handleLoading(true);
      await callAPIs(
        [
          defaultBrandManager.getRecommendBrandOptions.bind(
            defaultBrandManager,
          ),
        ],
        (brandOptions) => {
          // 必須過濾掉"其他"品牌
          const preprocessBrandOptions = brandOptions.filter(
            ({ value }) => value !== '99999999999999999',
          );
          if (preprocessBrandOptions.length === 0) {
            setLockRecommend(true);
          } else {
            setLockRecommend(false);
          }
        },
      );
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      handleLoading(false);
    }
  };

  useEffect(() => {
    fetchBrandOptions();
    fetchRecommendBrandOptions();
  }, []);

  const list = checkPermission(campaignType);
  const top = list.slice(0, 3);
  const bottom = list.slice(3, 5);

  return (
    <Section title='選擇廣告活動類型' position={'center'}>
      <div className={styles.adTypeCardContainer}>
        {list.length <= 4 && (
          <CardGroup
            columns={2}
            spacing={60}
            className={styles.adTypeCardGroup}
          >
            {list.map((item, index) => (
              <Card
                key={index}
                shaded='hover'
                className={`${styles.adTypeCard} ${
                  (lockShop && item.adType === AdType.SHOP) ||
                  (lockRecommend && item.adType === AdType.RECOMMEND)
                    ? styles['adTypeCard--lock']
                    : ''
                }`}
              >
                <Card.Body className={`${styles['adTypeCard--body']}`}>
                  {item.isNew && (
                    <div>
                      <img
                        src={NewLogo}
                        width={50}
                        height={42}
                        className={styles['adTypeCard--new-logo']}
                        alt='New Logo'
                      />
                    </div>
                  )}
                  <img
                    src={item.logo}
                    alt='Shadow'
                    width={110}
                    height={80}
                    onClick={() => selectdAdType(item)}
                  />
                  <h3
                    className={styles['adTypeCard--title']}
                    onClick={() => selectdAdType(item)}
                  >
                    {item.title}
                  </h3>
                  {item.description}
                </Card.Body>
                {lockRecommend && item.adType === AdType.RECOMMEND && (
                  <div className='absolute inset-x-0 bottom-0 flex items-center justify-center gap-2 bg-white px-4 py-2 text-sm text-[#252B37]'>
                    <InfoOutlineIcon />
                    {item.lockHint}
                  </div>
                )}
              </Card>
            ))}
          </CardGroup>
        )}
        {list.length >= 5 && (
          <>
            <CardGroup
              columns={3}
              spacing={32}
              className={styles.adTypeCardGroupTop}
            >
              {top.map((item, index) => (
                <Card
                  key={index}
                  shaded='hover'
                  className={`${styles.adTypeCard} ${
                    (lockShop && item.adType === AdType.SHOP) ||
                    (lockRecommend && item.adType === AdType.RECOMMEND)
                      ? styles['adTypeCard--lock']
                      : ''
                  }`}
                >
                  <Card.Body className={`${styles['adTypeCard--body']}`}>
                    {item.isNew && (
                      <div>
                        <img
                          src={NewLogo}
                          width={50}
                          height={42}
                          className={styles['adTypeCard--new-logo']}
                          alt='New Logo'
                        />
                      </div>
                    )}
                    <img
                      src={item.logo}
                      alt='Shadow'
                      width={110}
                      height={80}
                      onClick={() => selectdAdType(item)}
                    />
                    <h3
                      className={styles['adTypeCard--title']}
                      onClick={() => selectdAdType(item)}
                    >
                      {item.title}
                    </h3>
                    {item.description}
                  </Card.Body>
                  {lockRecommend && item.adType === AdType.RECOMMEND && (
                    <div className='absolute inset-x-0 bottom-0 flex items-center justify-center gap-2 bg-white px-4 py-2 text-sm text-[#252B37]'>
                      <InfoOutlineIcon />
                      {item.lockHint}
                    </div>
                  )}
                </Card>
              ))}
            </CardGroup>
            <CardGroup
              columns={2}
              spacing={32}
              className={styles.adTypeCardGroupBottom}
            >
              {bottom.map((item, index) => (
                <Card
                  key={index}
                  shaded='hover'
                  className={`${styles.adTypeCard} ${
                    (lockShop && item.adType === AdType.SHOP) ||
                    (lockRecommend && item.adType === AdType.RECOMMEND)
                      ? styles['adTypeCard--lock']
                      : ''
                  }`}
                >
                  <Card.Body className={styles['adTypeCard--body']}>
                    {item.isNew && (
                      <div>
                        <img
                          src={NewLogo}
                          width={50}
                          height={42}
                          className={styles['adTypeCard--new-logo']}
                          alt='New Logo'
                        />
                      </div>
                    )}
                    <img
                      src={item.logo}
                      alt='Shadow'
                      width={110}
                      height={80}
                      onClick={() => selectdAdType(item)}
                    />
                    <h3
                      className={styles['adTypeCard--title']}
                      onClick={() => selectdAdType(item)}
                    >
                      {item.title}
                    </h3>
                    {item.description}
                  </Card.Body>
                  {lockRecommend && item.adType === AdType.RECOMMEND && (
                    <div className='absolute inset-x-0 bottom-0 flex items-center justify-center gap-2 bg-white px-4 py-2 text-sm text-[#252B37]'>
                      <InfoOutlineIcon />
                      {item.lockHint}
                    </div>
                  )}
                </Card>
              ))}
            </CardGroup>
          </>
        )}
      </div>
    </Section>
  );
};

export default RtbCampaignSelectAdType;
